import React, { useState, useEffect } from 'react';
import { COLORS } from '../../../core/constants/colors';
import { api } from '../../../core/utils/apiClient';

interface CompanyMember {
  id: number;
  company_id: number;
  user_id: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  joined_at: string;
  is_active: boolean;
  invited_by: string;
  email?: string | null;
  full_name?: string | null;
}

interface DomainSettings {
  auto_join_enabled: boolean;
  auto_join_role: 'owner' | 'admin' | 'member' | 'viewer';
  privileged_email_prefixes: string[];
  updated_at: string;
}

interface AuditLog {
  id: number;
  company_id: number;
  action: string;
  target_user_id: string;
  field_name: string;
  old_value: string;
  new_value: string;
  performed_by: string;
  extra_metadata?: any;
  created_at: string;
}

interface OwnershipTransfer {
  id: number;
  company_id: number;
  previous_owner_id: string;
  new_owner_id: string;
  initiated_by: string;
  transfer_type: string;
  notification_sent_old_owner: boolean;
  notification_sent_new_owner: boolean;
  notes: string;
  created_at: string;
}

interface Props {
  companyId: number;
  currentUserRole: string;
  onClose: () => void;
}

export const CompanyMembersManager: React.FC<Props> = ({ companyId, currentUserRole, onClose }) => {
  const [activeTab, setActiveTab] = useState<'members' | 'settings' | 'audit'>('members');
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [domainSettings, setDomainSettings] = useState<DomainSettings | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [ownershipTransfers, setOwnershipTransfers] = useState<OwnershipTransfer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add Member Modal State
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [newMemberUserId, setNewMemberUserId] = useState('');
  const [newMemberFirstName, setNewMemberFirstName] = useState('');
  const [newMemberLastName, setNewMemberLastName] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const [sendInvitationEmail, setSendInvitationEmail] = useState(true);

  // Edit Member Modal State
  const [editingMember, setEditingMember] = useState<CompanyMember | null>(null);
  const [editMemberRole, setEditMemberRole] = useState<'owner' | 'admin' | 'member' | 'viewer'>('member');

  // Transfer Ownership Modal State
  const [showTransferOwnershipModal, setShowTransferOwnershipModal] = useState(false);
  const [transferToUserId, setTransferToUserId] = useState('');
  const [transferNotes, setTransferNotes] = useState('');

  // Domain Settings Edit State
  const [editingDomainSettings, setEditingDomainSettings] = useState(false);
  const [autoJoinEnabled, setAutoJoinEnabled] = useState(false);
  const [autoJoinRole, setAutoJoinRole] = useState<'member' | 'viewer'>('member');
  const [privilegedPrefixes, setPrivilegedPrefixes] = useState<string[]>([]);
  const [newPrefix, setNewPrefix] = useState('');

  useEffect(() => {
    if (activeTab === 'members') {
      loadMembers();
    } else if (activeTab === 'settings') {
      loadDomainSettings();
    } else if (activeTab === 'audit') {
      loadAuditLogs();
      loadOwnershipTransfers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, companyId]);

  const loadMembers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(`/api/v1/companies/${companyId}/members`);
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      } else {
        setError('Failed to load members');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load members');
    } finally {
      setIsLoading(false);
    }
  };

  const loadDomainSettings = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(`/api/v1/companies/${companyId}/domain-settings`);
      if (response.ok) {
        const data = await response.json();
        setDomainSettings(data);
        setAutoJoinEnabled(data.auto_join_enabled);
        setAutoJoinRole(data.auto_join_role);
        setPrivilegedPrefixes(data.privileged_email_prefixes || []);
      } else {
        setError('Failed to load domain settings');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load domain settings');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const response = await api.get(`/api/v1/companies/${companyId}/audit-logs?limit=50`);
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data);
      }
    } catch (err: any) {
      console.error('Failed to load audit logs:', err);
    }
  };

  const loadOwnershipTransfers = async () => {
    try {
      const response = await api.get(`/api/v1/companies/${companyId}/ownership-transfers`);
      if (response.ok) {
        const data = await response.json();
        setOwnershipTransfers(data);
      }
    } catch (err: any) {
      console.error('Failed to load ownership transfers:', err);
    }
  };

  const handleAddMember = async () => {
    try {
      setError(null);
      const response = await api.post(`/api/v1/companies/${companyId}/members`, {
        user_id: newMemberUserId,
        role: newMemberRole,
        send_invitation_email: sendInvitationEmail,
        first_name: newMemberFirstName.trim() || undefined,
        last_name: newMemberLastName.trim() || undefined
      });

      if (response.ok) {
        setShowAddMemberModal(false);
        setNewMemberUserId('');
        setNewMemberFirstName('');
        setNewMemberLastName('');
        setNewMemberRole('member');
        setSendInvitationEmail(true);
        loadMembers();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to add member');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add member');
    }
  };

  const handleUpdateMemberRole = async () => {
    if (!editingMember) return;

    try {
      setError(null);
      const response = await api.put(
        `/api/v1/companies/${companyId}/members/${editingMember.user_id}`,
        { role: editMemberRole }
      );

      if (response.ok) {
        setEditingMember(null);
        loadMembers();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update member role');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update member role');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;

    try {
      setError(null);
      const response = await api.delete(`/api/v1/companies/${companyId}/members/${userId}`);

      if (response.ok) {
        loadMembers();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to remove member');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to remove member');
    }
  };

  const handleClaimOwnership = async () => {
    if (!window.confirm('Are you sure you want to claim ownership of this company?')) return;

    try {
      setError(null);
      const response = await api.post(`/api/v1/companies/${companyId}/claim-ownership`, {
        notes: 'Ownership claimed'
      });

      if (response.ok) {
        loadMembers();
        alert('Ownership claimed successfully');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to claim ownership');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to claim ownership');
    }
  };

  const handleTransferOwnership = async () => {
    try {
      setError(null);
      const response = await api.post(`/api/v1/companies/${companyId}/transfer-ownership`, {
        new_owner_id: transferToUserId,
        notes: transferNotes
      });

      if (response.ok) {
        setShowTransferOwnershipModal(false);
        setTransferToUserId('');
        setTransferNotes('');
        loadMembers();
        alert('Ownership transferred successfully');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to transfer ownership');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to transfer ownership');
    }
  };

  const handleUpdateDomainSettings = async () => {
    try {
      setError(null);
      const response = await api.put(`/api/v1/companies/${companyId}/domain-settings`, {
        auto_join_enabled: autoJoinEnabled,
        auto_join_role: autoJoinRole,
        privileged_email_prefixes: privilegedPrefixes
      });

      if (response.ok) {
        setEditingDomainSettings(false);
        loadDomainSettings();
        alert('Domain settings updated successfully');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update domain settings');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update domain settings');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner': return { bg: '#FEF3C7', text: '#92400E' };
      case 'admin': return { bg: '#DBEAFE', text: '#1E40AF' };
      case 'member': return { bg: '#E0E7FF', text: '#3730A3' };
      case 'viewer': return { bg: '#F3F4F6', text: '#374151' };
      default: return { bg: '#F3F4F6', text: '#6B7280' };
    }
  };

  const canManageMembers = currentUserRole === 'owner' || currentUserRole === 'admin';
  const isOwner = currentUserRole === 'owner';

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Side Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b flex items-center justify-between" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold" style={{ color: '#1F2937' }}>Manage Company</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-all"
              style={{ color: '#6B7280' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-6 px-6 border-b" style={{ borderColor: '#E5E7EB' }}>
          <button
            onClick={() => setActiveTab('members')}
            className={`pb-3 px-1 text-sm font-medium transition-colors ${
              activeTab === 'members' ? 'border-b-2' : ''
            }`}
            style={{
              color: activeTab === 'members' ? COLORS.user.primary : '#6B7280',
              borderColor: activeTab === 'members' ? COLORS.user.primary : 'transparent'
            }}
          >
            Members
          </button>
          {canManageMembers && (
            <button
              onClick={() => setActiveTab('settings')}
              className={`pb-3 px-1 text-sm font-medium transition-colors ${
                activeTab === 'settings' ? 'border-b-2' : ''
              }`}
              style={{
                color: activeTab === 'settings' ? COLORS.user.primary : '#6B7280',
                borderColor: activeTab === 'settings' ? COLORS.user.primary : 'transparent'
              }}
            >
              Domain Settings
            </button>
          )}
          {canManageMembers && (
            <button
              onClick={() => setActiveTab('audit')}
              className={`pb-3 px-1 text-sm font-medium transition-colors ${
                activeTab === 'audit' ? 'border-b-2' : ''
              }`}
              style={{
                color: activeTab === 'audit' ? COLORS.user.primary : '#6B7280',
                borderColor: activeTab === 'audit' ? COLORS.user.primary : 'transparent'
              }}
            >
              Audit Logs
            </button>
          )}
        </div>

          {/* Error Display */}
          {error && (
            <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
          {activeTab === 'members' && (
            <div>
              {/* Action Buttons */}
              <div className="flex gap-3 mb-6">
                {canManageMembers && (
                  <button
                    onClick={() => setShowAddMemberModal(true)}
                    className="px-4 py-2 rounded-lg text-white font-medium text-sm transition-opacity hover:opacity-90"
                    style={{ backgroundColor: COLORS.user.primary }}
                  >
                    Add Member
                  </button>
                )}
                {isOwner && (
                  <button
                    onClick={() => setShowTransferOwnershipModal(true)}
                    className="px-4 py-2 rounded-lg font-medium text-sm border transition-colors hover:bg-gray-50"
                    style={{ borderColor: '#E5E7EB', color: '#374151' }}
                  >
                    Transfer Ownership
                  </button>
                )}
                {currentUserRole === 'admin' && !members.some(m => m.role === 'owner') && (
                  <button
                    onClick={handleClaimOwnership}
                    className="px-4 py-2 rounded-lg font-medium text-sm border transition-colors hover:bg-gray-50"
                    style={{ borderColor: '#E5E7EB', color: '#374151' }}
                  >
                    Claim Ownership
                  </button>
                )}
              </div>

              {/* Members List */}
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div
                    className="animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
                    style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  {members.map((member) => {
                    const roleColors = getRoleBadgeColor(member.role);
                    return (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-4 rounded-lg border"
                        style={{ borderColor: '#E5E7EB' }}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{member.full_name || member.email || member.user_id}</p>
                              {member.email && (member.full_name || member.email !== member.user_id) && (
                                <p className="text-xs text-gray-500">{member.email}</p>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                Joined {new Date(member.joined_at).toLocaleDateString()}
                              </p>
                            </div>
                            <span
                              className="px-3 py-1 rounded-full text-xs font-medium capitalize"
                              style={{ backgroundColor: roleColors.bg, color: roleColors.text }}
                            >
                              {member.role}
                            </span>
                            {!member.is_active && (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
                                Inactive
                              </span>
                            )}
                          </div>
                        </div>
                        {canManageMembers && member.role !== 'owner' && (
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => {
                                setEditingMember(member);
                                setEditMemberRole(member.role);
                              }}
                              className="px-3 py-1 text-sm rounded border hover:bg-gray-50"
                              style={{ borderColor: '#E5E7EB', color: '#374151' }}
                            >
                              Edit Role
                            </button>
                            <button
                              onClick={() => handleRemoveMember(member.user_id)}
                              className="px-3 py-1 text-sm rounded text-red-600 border border-red-200 hover:bg-red-50"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900">Domain-Based Auto-Join Settings</h3>
                {!editingDomainSettings && (
                  <button
                    onClick={() => setEditingDomainSettings(true)}
                    className="px-4 py-2 rounded-lg text-white font-medium text-sm"
                    style={{ backgroundColor: COLORS.user.primary }}
                  >
                    Edit Settings
                  </button>
                )}
              </div>

              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div
                    className="animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
                    style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
                  />
                </div>
              ) : domainSettings ? (
                <div className="space-y-6">
                  <div className="p-4 rounded-lg border" style={{ borderColor: '#E5E7EB' }}>
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={autoJoinEnabled}
                        onChange={(e) => setAutoJoinEnabled(e.target.checked)}
                        disabled={!editingDomainSettings}
                        className="w-4 h-4"
                      />
                      <div>
                        <p className="font-medium text-gray-900">Enable Auto-Join</p>
                        <p className="text-xs text-gray-500">
                          Allow users with matching email domain to automatically join
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="p-4 rounded-lg border" style={{ borderColor: '#E5E7EB' }}>
                    <label className="block mb-2">
                      <span className="text-sm font-medium text-gray-700">Default Auto-Join Role</span>
                    </label>
                    <select
                      value={autoJoinRole}
                      onChange={(e) => setAutoJoinRole(e.target.value as 'member' | 'viewer')}
                      disabled={!editingDomainSettings}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      style={{ borderColor: '#E5E7EB' }}
                    >
                      <option value="member">Member</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </div>

                  <div className="p-4 rounded-lg border" style={{ borderColor: '#E5E7EB' }}>
                    <label className="block mb-2">
                      <span className="text-sm font-medium text-gray-700">Privileged Email Prefixes</span>
                      <p className="text-xs text-gray-500 mt-1">
                        Users with these email prefixes will get admin role (e.g., admin@, ceo@, owner@)
                      </p>
                    </label>
                    <div className="space-y-2">
                      {privilegedPrefixes.map((prefix, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={prefix}
                            disabled={!editingDomainSettings}
                            className="flex-1 px-3 py-2 border rounded-lg text-sm"
                            style={{ borderColor: '#E5E7EB' }}
                          />
                          {editingDomainSettings && (
                            <button
                              onClick={() => {
                                setPrivilegedPrefixes(privilegedPrefixes.filter((_, i) => i !== index));
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                      {editingDomainSettings && (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={newPrefix}
                            onChange={(e) => setNewPrefix(e.target.value)}
                            placeholder="e.g., admin@"
                            className="flex-1 px-3 py-2 border rounded-lg text-sm"
                            style={{ borderColor: '#E5E7EB' }}
                          />
                          <button
                            onClick={() => {
                              if (newPrefix.trim()) {
                                setPrivilegedPrefixes([...privilegedPrefixes, newPrefix.trim()]);
                                setNewPrefix('');
                              }
                            }}
                            className="px-3 py-2 rounded-lg text-white text-sm"
                            style={{ backgroundColor: COLORS.user.primary }}
                          >
                            Add
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {editingDomainSettings && (
                    <div className="flex gap-3">
                      <button
                        onClick={handleUpdateDomainSettings}
                        className="px-6 py-2 rounded-lg text-white font-medium"
                        style={{ backgroundColor: COLORS.user.primary }}
                      >
                        Save Settings
                      </button>
                      <button
                        onClick={() => {
                          setEditingDomainSettings(false);
                          loadDomainSettings();
                        }}
                        className="px-6 py-2 rounded-lg border font-medium"
                        style={{ borderColor: '#E5E7EB' }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  <div className="text-xs text-gray-500">
                    Last updated: {new Date(domainSettings.updated_at).toLocaleString()}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">No domain settings found</p>
              )}
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="space-y-6">
              {/* Ownership Transfers */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Ownership Transfer History</h3>
                {ownershipTransfers.length > 0 ? (
                  <div className="space-y-3">
                    {ownershipTransfers.map((transfer) => (
                      <div
                        key={transfer.id}
                        className="p-4 rounded-lg border"
                        style={{ borderColor: '#E5E7EB' }}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {transfer.previous_owner_id} → {transfer.new_owner_id}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Type: {transfer.transfer_type} • Initiated by: {transfer.initiated_by}
                            </p>
                            {transfer.notes && (
                              <p className="text-xs text-gray-600 mt-1">{transfer.notes}</p>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(transfer.created_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No ownership transfers found</p>
                )}
              </div>

              {/* Audit Logs */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Audit Logs</h3>
                {auditLogs.length > 0 ? (
                  <div className="space-y-2">
                    {auditLogs.map((log) => (
                      <div
                        key={log.id}
                        className="p-3 rounded-lg border"
                        style={{ borderColor: '#E5E7EB' }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{log.action}</p>
                            {log.field_name && (
                              <p className="text-xs text-gray-600 mt-1">
                                {log.field_name}: {log.old_value} → {log.new_value}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              By: {log.performed_by} • Target: {log.target_user_id}
                            </p>
                          </div>
                          <span className="text-xs text-gray-500 ml-4">
                            {new Date(log.created_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No audit logs found</p>
                )}
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Add New Member</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">User ID (Email)</label>
                <input
                  type="text"
                  value={newMemberUserId}
                  onChange={(e) => setNewMemberUserId(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{ borderColor: '#E5E7EB' }}
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">First name <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={newMemberFirstName}
                    onChange={(e) => setNewMemberFirstName(e.target.value)}
                    placeholder="Jane"
                    className="w-full px-3 py-2 border rounded-lg"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last name <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={newMemberLastName}
                    onChange={(e) => setNewMemberLastName(e.target.value)}
                    placeholder="Doe"
                    className="w-full px-3 py-2 border rounded-lg"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value as 'admin' | 'member' | 'viewer')}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{ borderColor: '#E5E7EB' }}
                >
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={sendInvitationEmail}
                  onChange={(e) => setSendInvitationEmail(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-700">Send invitation email</span>
              </label>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleAddMember}
                  className="flex-1 px-4 py-2 rounded-lg text-white font-medium"
                  style={{ backgroundColor: COLORS.user.primary }}
                >
                  Add Member
                </button>
                <button
                  onClick={() => setShowAddMemberModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border font-medium"
                  style={{ borderColor: '#E5E7EB' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Role Modal */}
      {editingMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Edit Member Role</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">User</label>
                <p className="text-sm text-gray-900">{editingMember.user_id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Role</label>
                <select
                  value={editMemberRole}
                  onChange={(e) => setEditMemberRole(e.target.value as any)}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{ borderColor: '#E5E7EB' }}
                >
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleUpdateMemberRole}
                  className="flex-1 px-4 py-2 rounded-lg text-white font-medium"
                  style={{ backgroundColor: COLORS.user.primary }}
                >
                  Update Role
                </button>
                <button
                  onClick={() => setEditingMember(null)}
                  className="flex-1 px-4 py-2 rounded-lg border font-medium"
                  style={{ borderColor: '#E5E7EB' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Ownership Modal */}
      {showTransferOwnershipModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Transfer Ownership</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Owner User ID</label>
                <input
                  type="text"
                  value={transferToUserId}
                  onChange={(e) => setTransferToUserId(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{ borderColor: '#E5E7EB' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
                <textarea
                  value={transferNotes}
                  onChange={(e) => setTransferNotes(e.target.value)}
                  placeholder="Reason for transfer..."
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{ borderColor: '#E5E7EB' }}
                />
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  Warning: You will be downgraded to Admin after transferring ownership.
                </p>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleTransferOwnership}
                  className="flex-1 px-4 py-2 rounded-lg text-white font-medium"
                  style={{ backgroundColor: COLORS.user.primary }}
                >
                  Transfer Ownership
                </button>
                <button
                  onClick={() => setShowTransferOwnershipModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border font-medium"
                  style={{ borderColor: '#E5E7EB' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
